import { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { EmailConfirmationModal } from './EmailConfirmationModal';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { Turnstile, TurnstileRef } from './Turnstile';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { supabase } from '../../lib/supabase';

const API_BASE = import.meta.env.VITE_AI_WORKER_URL || '';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

// Official Google "G" multi-color icon
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileRef>(null);
  const { signIn, signInWithGoogle, resendConfirmationEmail, sendPasswordResetEmail } = useAuth();
  const analytics = useAnalytics();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!turnstileToken) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);

    try {
      // Verify Turnstile token with backend
      const verifyResponse = await fetch(`${API_BASE}/auth/verify-turnstile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: turnstileToken }),
      });

      if (!verifyResponse.ok) {
        const verifyData = await verifyResponse.json().catch(() => ({}));
        setError((verifyData as { error?: string }).error || 'Verification failed. Please try again.');
        setTurnstileToken(null);
        turnstileRef.current?.reset();
        return;
      }

      // Proceed with Supabase sign in
      const { error, emailNotConfirmed } = await signIn(email, password);
      if (error) {
        if (emailNotConfirmed) {
          // Show email confirmation modal
          setShowEmailConfirmation(true);
        } else {
          setError(error.message);
          // Reset turnstile on failed login
          setTurnstileToken(null);
          turnstileRef.current?.reset();
        }
      } else {
        // Login successful - track the event
        // Get the user ID from the current session
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          analytics.login({
            method: 'email',
            userId: user.id,
          });
        }
      }
    } catch {
      setError('An error occurred. Please try again.');
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    const { error } = await resendConfirmationEmail(email);
    if (error) {
      throw error;
    }
  };

  const handleCloseConfirmationModal = () => {
    setShowEmailConfirmation(false);
    setEmail('');
    setPassword('');
  };

  const handleSendPasswordReset = async (resetEmail: string) => {
    const { error } = await sendPasswordResetEmail(resetEmail);
    if (error) {
      throw error;
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      // Track Google login attempt (user will be redirected to Google OAuth)
      // The actual user ID will be tracked after the OAuth redirect completes
      analytics.login({ method: 'google', userId: 'pending-oauth' });

      const { error } = await signInWithGoogle();
      if (error) {
        setError(error.message);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Google Sign-In Button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
          className="w-full bg-white hover:bg-alabaster border border-stone text-charcoal py-4 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-3"
        >
          {googleLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-charcoal border-t-transparent rounded-full animate-spin" />
              <span>Connecting...</span>
            </>
          ) : (
            <>
              <GoogleIcon className="w-5 h-5" />
              <span>Continue with Google</span>
            </>
          )}
        </button>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-stone"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-sand font-light">or</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 mt-6">
        {error && (
          <div className="bg-terracotta/10 border border-terracotta/30 text-terracotta px-4 py-3 rounded-2xl text-sm font-light flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-xs font-mono uppercase tracking-widest text-sand mb-3">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-5 py-3.5 bg-alabaster border border-stone rounded-full text-charcoal placeholder:text-antique focus:outline-none focus:border-olive focus:ring-2 focus:ring-olive/10 transition-all duration-300 font-light"
            placeholder="you@company.com"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="password" className="block text-xs font-mono uppercase tracking-widest text-sand">
              Password
            </label>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-xs text-sand hover:text-terracotta font-light transition-colors duration-300"
            >
              Forgot password?
            </button>
          </div>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-5 py-3.5 bg-alabaster border border-stone rounded-full text-charcoal placeholder:text-antique focus:outline-none focus:border-olive focus:ring-2 focus:ring-olive/10 transition-all duration-300 font-light"
            placeholder="••••••••"
          />
        </div>

        {/* Turnstile CAPTCHA */}
        {TURNSTILE_SITE_KEY && (
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken(null)}
            onExpired={() => setTurnstileToken(null)}
          />
        )}

        <button
          type="submit"
          disabled={loading || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
          className="w-full bg-olive hover:bg-charcoal text-alabaster py-4 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-alabaster border-t-transparent rounded-full animate-spin" />
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <span>Sign In</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>

      <div className="mt-8 text-center border-t border-stone pt-6">
        <p className="text-sand font-light">
          Don't have an account?{' '}
          <button
            onClick={onSwitchToSignup}
            className="text-charcoal hover:text-terracotta font-medium transition-colors duration-300"
          >
            Sign up
          </button>
        </p>
      </div>

      {showEmailConfirmation && (
        <EmailConfirmationModal
          email={email}
          onClose={handleCloseConfirmationModal}
          onResend={handleResendConfirmation}
        />
      )}

      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
          onSendResetEmail={handleSendPasswordReset}
        />
      )}
    </>
  );
}
